import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Heading,
  InlineStack,
  Modal,
  ModalBody,
  ModalFooter,
  Stack,
  Text,
  TextField,
  useModal,
  useSnackbars,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import type { ObjectType } from '@/services/db/object-type/entity';
import { objectName, type ObjectRecord } from '@/services/db/object/entity';
import type { Customer } from '@/services/db/customer/entity';
import { getPeopleQueryOptions } from '@/services/db/customer/queries';
import { getObjectsQueryOptions } from '@/services/db/object/queries';
import { useCreateRelationships } from '@/services/db/relationship/mutations';
import { useAnalytics } from '@/services/analytics';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { isEmptyString } from '@/utils/string';

type AddRelationshipsModalData = { objectType: ObjectType; workspaceId: number };
type Step = 'people' | 'objects' | 'attributes' | 'review';
const STEPS: Step[] = ['people', 'objects', 'attributes', 'review'];

function useRelationshipFormState() {
  const [step, setStep] = useState<Step>('people');
  const [peoplePage, setPeoplePage] = useState(1);
  const [objectsPage, setObjectsPage] = useState(1);
  const [peopleIds, setPeopleIds] = useState<string[]>([]);
  const [objectIds, setObjectIds] = useState<string[]>([]);
  const [attributeName, setAttributeName] = useState('');
  const [attributeValue, setAttributeValue] = useState('');
  const [attributes, setAttributes] = useState<Record<string, string>>({});

  return {
    step,
    setStep,
    peoplePage,
    setPeoplePage,
    objectsPage,
    setObjectsPage,
    peopleIds,
    setPeopleIds,
    objectIds,
    setObjectIds,
    attributeName,
    setAttributeName,
    attributeValue,
    setAttributeValue,
    attributes,
    setAttributes,
  };
}

function selectedIds(current: readonly string[], id: string, selected: boolean): string[] {
  return selected ? [...current, id] : current.filter((value) => value !== id);
}

type PageButtonsProps = {
  item: string;
  page: number;
  hasNextPage: boolean;
  onPageChange: (page: number) => void;
};

function PageButtons({ item, page, hasNextPage, onPageChange }: PageButtonsProps) {
  return (
    <InlineStack gap="100">
      <Button size="sm" isDisabled={page === 1} onClick={() => onPageChange(page - 1)}>
        Previous {item} page
      </Button>
      <Button size="sm" isDisabled={!hasNextPage} onClick={() => onPageChange(page + 1)}>
        Next {item} page
      </Button>
    </InlineStack>
  );
}

type PeopleStepProps = {
  people: Customer[];
  page: number;
  total: number;
  pageSize: number;
  selected: string[];
  onPageChange: (page: number) => void;
  onSelectionChange: (ids: string[]) => void;
};

function PeopleStep({ people, page, total, pageSize, selected, onPageChange, onSelectionChange }: PeopleStepProps) {
  return (
    <Stack gap="100" mt="200">
      {people.map((person) => {
        const label = person.attributes.name ?? person.identifiers.email ?? person.id;
        return (
          <Checkbox
            className="fs-mask"
            key={person.id}
            value={person.internal_id}
            label={label}
            isChecked={selected.includes(person.internal_id)}
            onCheckedChange={(checked) => onSelectionChange(selectedIds(selected, person.internal_id, checked))}
          />
        );
      })}
      <PageButtons item="people" page={page} hasNextPage={page * pageSize < total} onPageChange={onPageChange} />
    </Stack>
  );
}

type ObjectsStepProps = {
  objects: ObjectRecord[];
  page: number;
  total: number;
  pageSize: number;
  selected: string[];
  onPageChange: (page: number) => void;
  onSelectionChange: (ids: string[]) => void;
};

function ObjectsStep({ objects, page, total, pageSize, selected, onPageChange, onSelectionChange }: ObjectsStepProps) {
  return (
    <Stack gap="100" mt="200">
      {objects.map((object) => (
        <Checkbox
          className="fs-mask"
          key={object.id}
          value={object.id}
          label={objectName(object) ?? object.identifiers.object_id}
          isChecked={selected.includes(object.id)}
          onCheckedChange={(checked) => onSelectionChange(selectedIds(selected, object.id, checked))}
        />
      ))}
      <PageButtons item="objects" page={page} hasNextPage={page * pageSize < total} onPageChange={onPageChange} />
    </Stack>
  );
}

type AttributesStepProps = {
  name: string;
  value: string;
  attributes: Record<string, string>;
  onNameChange: (value: string) => void;
  onValueChange: (value: string) => void;
  onAdd: () => void;
  onRemove: (name: string) => void;
};

function AttributesStep({
  name,
  value,
  attributes,
  onNameChange,
  onValueChange,
  onAdd,
  onRemove,
}: AttributesStepProps) {
  return (
    <Stack gap="150" mt="200">
      <Text>Optionally add attributes to every relationship.</Text>
      <TextField label="Attribute name" value={name} onInput={(event) => onNameChange(event.target.value)} />
      <TextField label="Attribute value" value={value} onInput={(event) => onValueChange(event.target.value)} />
      <Button onClick={onAdd} isDisabled={isEmptyString(name)}>
        Add attribute
      </Button>
      {Object.entries(attributes).map(([attributeName, attributeValue]) => (
        <InlineStack key={attributeName} gap="100" alignBlock="center">
          <Text className="fs-mask">
            {attributeName}: {attributeValue}
          </Text>
          <Button size="sm" variant="tertiary" onClick={() => onRemove(attributeName)}>
            Remove
          </Button>
        </InlineStack>
      ))}
    </Stack>
  );
}

type ReviewStepProps = {
  peopleCount: number;
  objectCount: number;
  attributeCount: number;
  peopleName: string;
  objectName: string;
};

function ReviewStep({ peopleCount, objectCount, attributeCount, peopleName, objectName }: ReviewStepProps) {
  return (
    <Stack gap="100" mt="200">
      <Heading level="3">Review</Heading>
      <Text>
        You are about to add {peopleCount * objectCount} relationships between {peopleCount} {peopleName} and{' '}
        {objectCount} {objectName}.
      </Text>
      <Text>{attributeCount} relationship attributes will be added.</Text>
    </Stack>
  );
}

type RelationshipStepProps = {
  step: Step;
  people: Customer[];
  peoplePage: number;
  peopleTotal: number;
  peoplePageSize: number;
  peopleIds: string[];
  objects: ObjectRecord[];
  objectsPage: number;
  objectsTotal: number;
  objectsPageSize: number;
  objectIds: string[];
  attributeName: string;
  attributeValue: string;
  attributes: Record<string, string>;
  peopleName: string;
  objectName: string;
  onPeoplePageChange: (page: number) => void;
  onPeopleSelectionChange: (ids: string[]) => void;
  onObjectsPageChange: (page: number) => void;
  onObjectsSelectionChange: (ids: string[]) => void;
  onAttributeNameChange: (value: string) => void;
  onAttributeValueChange: (value: string) => void;
  onAddAttribute: () => void;
  onRemoveAttribute: (name: string) => void;
};

// fallow-ignore-next-line complexity
function RelationshipStep({
  step,
  people,
  peoplePage,
  peopleTotal,
  peoplePageSize,
  peopleIds,
  objects,
  objectsPage,
  objectsTotal,
  objectsPageSize,
  objectIds,
  attributeName,
  attributeValue,
  attributes,
  peopleName,
  objectName,
  onPeoplePageChange,
  onPeopleSelectionChange,
  onObjectsPageChange,
  onObjectsSelectionChange,
  onAttributeNameChange,
  onAttributeValueChange,
  onAddAttribute,
  onRemoveAttribute,
}: RelationshipStepProps) {
  switch (step) {
    case 'people':
      return (
        <PeopleStep
          people={people}
          page={peoplePage}
          total={peopleTotal}
          pageSize={peoplePageSize}
          selected={peopleIds}
          onPageChange={onPeoplePageChange}
          onSelectionChange={onPeopleSelectionChange}
        />
      );
    case 'objects':
      return (
        <ObjectsStep
          objects={objects}
          page={objectsPage}
          total={objectsTotal}
          pageSize={objectsPageSize}
          selected={objectIds}
          onPageChange={onObjectsPageChange}
          onSelectionChange={onObjectsSelectionChange}
        />
      );
    case 'attributes':
      return (
        <AttributesStep
          name={attributeName}
          value={attributeValue}
          attributes={attributes}
          onNameChange={onAttributeNameChange}
          onValueChange={onAttributeValueChange}
          onAdd={onAddAttribute}
          onRemove={onRemoveAttribute}
        />
      );
    case 'review':
      return (
        <ReviewStep
          peopleCount={peopleIds.length}
          objectCount={objectIds.length}
          attributeCount={Object.keys(attributes).length}
          peopleName={peopleName}
          objectName={objectName}
        />
      );
    default: {
      const _exhaustive: never = step;
      return _exhaustive;
    }
  }
}

function canContinue(step: Step, peopleIds: readonly string[], objectIds: readonly string[]): boolean {
  if (step === 'people') return peopleIds.length > 0;
  if (step === 'objects') return objectIds.length > 0;
  return true;
}

function relationshipInputs(
  peopleIds: readonly string[],
  objectIds: readonly string[],
  attributes: Record<string, string>,
) {
  return objectIds.flatMap((objectId) =>
    peopleIds.map((personId) => ({
      entity1_type: 'customer',
      entity1_id: personId,
      entity2_type: 'object',
      entity2_id: objectId,
      attributes,
      created_at: Math.floor(Date.now() / 1000),
    })),
  );
}

// fallow-ignore-next-line complexity
function AddRelationshipsModal({
  data,
  modalState,
  onClose,
  onConfirm,
}: PlumaManagedModalProps<AddRelationshipsModalData>) {
  const qc = useQueryClient();
  const taxonomy = useProductTaxonomy();
  const { track } = useAnalytics();
  const snackbars = useSnackbars();
  const form = useRelationshipFormState();
  const people = useQuery(getPeopleQueryOptions(data.workspaceId, { page: form.peoplePage }));
  const objects = useQuery(
    getObjectsQueryOptions(data.workspaceId, { objectTypeId: data.objectType.id, page: form.objectsPage }, qc),
  );
  const create = useCreateRelationships({
    mutationConfig: {
      onSuccess: () => {
        snackbars.create({ id: 'relationships.add', message: 'Relationships added' });
        void track('objects', 'relationshipsManualAddCompleted', {
          Source: 'Object List',
          'Number of People': form.peopleIds.length,
          'Number of Objects': form.objectIds.length,
          'Number of Relationship Attributes': Object.keys(form.attributes).length,
        });
        onConfirm();
      },
      onError: () =>
        snackbars.create({
          id: 'relationships.add',
          variant: 'critical',
          message: 'There was an error creating relationships. Try again later.',
        }),
    },
  });

  useEffect(() => {
    void track('objects', 'relationshipsManualAddStarted', { Source: 'Object List' });
  }, [track]);

  const currentStep = STEPS.indexOf(form.step);
  const addAttribute = () => {
    if (isEmptyString(form.attributeName)) return;
    form.setAttributes((current) => ({ ...current, [form.attributeName]: form.attributeValue }));
    form.setAttributeName('');
    form.setAttributeValue('');
  };
  const submit = () => create.mutate(relationshipInputs(form.peopleIds, form.objectIds, form.attributes));

  return (
    <Modal state={modalState} title="Add relationships" isDismissable={!create.isPending}>
      <ModalBody>
        <Text color="subtle">
          {currentStep + 1}. {form.step === 'people' ? `Select ${taxonomy.term('people.titlePlural')}` : form.step}
        </Text>
        <RelationshipStep
          step={form.step}
          people={people.data?.customers ?? []}
          peoplePage={form.peoplePage}
          peopleTotal={people.data?.meta.pagination.total ?? 0}
          peoplePageSize={people.data?.meta.pagination.size ?? 50}
          peopleIds={form.peopleIds}
          objects={objects.data?.objects ?? []}
          objectsPage={form.objectsPage}
          objectsTotal={objects.data?.meta.pagination.total ?? 0}
          objectsPageSize={objects.data?.meta.pagination.size ?? 50}
          objectIds={form.objectIds}
          attributeName={form.attributeName}
          attributeValue={form.attributeValue}
          attributes={form.attributes}
          peopleName={taxonomy.term('people.titlePlural')}
          objectName={data.objectType.name}
          onPeoplePageChange={form.setPeoplePage}
          onPeopleSelectionChange={form.setPeopleIds}
          onObjectsPageChange={form.setObjectsPage}
          onObjectsSelectionChange={form.setObjectIds}
          onAttributeNameChange={form.setAttributeName}
          onAttributeValueChange={form.setAttributeValue}
          onAddAttribute={addAttribute}
          onRemoveAttribute={(name) => form.setAttributes(({ [name]: _removed, ...rest }) => rest)}
        />
      </ModalBody>
      <ModalFooter>
        <Button
          variant="secondary"
          onClick={() => (currentStep === 0 ? onClose() : form.setStep(STEPS[currentStep - 1] ?? 'people'))}
          isDisabled={create.isPending}
        >
          {currentStep === 0 ? 'Cancel' : 'Back'}
        </Button>
        <Button
          variant="primary"
          isDisabled={!canContinue(form.step, form.peopleIds, form.objectIds)}
          isLoading={create.isPending}
          onClick={() => (form.step === 'review' ? submit() : form.setStep(STEPS[currentStep + 1] ?? 'review'))}
        >
          {form.step === 'review' ? 'Add relationships' : 'Next'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export function useAddRelationshipsModal() {
  return useModal(AddRelationshipsModal);
}
